import React from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  Fab,
  IconButton,
  Paper,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SettingsIcon from '@mui/icons-material/Settings'
import VisibilityIcon from '@mui/icons-material/Visibility'

import type { IBlock } from '../../types'
import type { SalepageThemeValues } from '../SalepageDetail/SeoThemeTab'
import { BlockRenderer } from './BlockRenderer'
import { PreviewThemeForm } from './PreviewThemeForm'

const CONFIG_WIDTH = 360

interface SalepagePreviewModalProps {
  open: boolean
  onClose: () => void
  blocks: IBlock[]
  title?: string
  theme?: SalepageThemeValues
  onSaveTheme?: (theme: SalepageThemeValues) => Promise<void>
}

function useThemeStyles(theme: SalepageThemeValues | undefined) {
  React.useEffect(() => {
    if (!theme?.fontUrl || !theme.fontUrl.startsWith('https://fonts.googleapis.com')) return
    const id = 'salepage-preview-font'
    let link = document.getElementById(id) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = theme.fontUrl
    return () => link?.remove()
  }, [theme?.fontUrl])

  if (!theme) return {}
  const bg: React.CSSProperties = {}
  if (theme.backgroundGradient) {
    bg.background = theme.backgroundGradient
  } else if (theme.backgroundImage) {
    bg.backgroundImage = `url(${theme.backgroundImage})`
    bg.backgroundSize = theme.backgroundSize || 'cover'
    bg.backgroundPosition = theme.backgroundPosition || 'center'
    bg.backgroundRepeat = (theme.backgroundRepeat as React.CSSProperties['backgroundRepeat']) || 'no-repeat'
    if (theme.backgroundColor) bg.backgroundColor = theme.backgroundColor
  } else if (theme.backgroundColor) {
    bg.backgroundColor = theme.backgroundColor
  }
  return {
    fontFamily: theme.fontFamily || undefined,
    color: theme.fontColor || undefined,
    ...bg,
  }
}

export const SalepagePreviewModal: React.FC<SalepagePreviewModalProps> = ({
  open,
  onClose,
  blocks,
  title,
  theme,
  onSaveTheme,
}) => {
  const sorted = [...blocks].sort((a, b) => a.order - b.order)
  const [configOpen, setConfigOpen] = React.useState(false)
  const [localTheme, setLocalTheme] = React.useState<SalepageThemeValues | undefined>(theme)
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setLocalTheme(theme)
  }, [theme, open])

  React.useEffect(() => {
    if (!open) setConfigOpen(false)
  }, [open])

  const effectiveTheme = configOpen ? localTheme : theme
  const themeStyles = useThemeStyles(effectiveTheme)

  const handleSaveTheme = async (values: SalepageThemeValues) => {
    if (!onSaveTheme) return
    try {
      setSaving(true)
      await onSaveTheme(values)
      setLocalTheme(values)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={configOpen ? 'xl' : 'md'}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          transition: 'max-width 0.3s ease',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <VisibilityIcon sx={{ color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            Preview{title ? ` — ${title}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            แสดงตัวอย่างข้อมูลปัจจุบัน (ยังไม่ได้บันทึก)
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', minHeight: 400 }}>
        {/* Preview - scrollable */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            position: 'relative',
            ...themeStyles,
          }}
        >
          {sorted.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                m: 3,
                p: 6,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                ยังไม่มี Content Block
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                maxWidth: 800,
                mx: 'auto',
                py: 3,
                px: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {sorted.map((block, idx) => (
                <Box key={`${block.type}-${idx}`}>
                  <BlockRenderer block={block} />
                </Box>
              ))}
            </Box>
          )}
          {!configOpen && (
            <Fab
              color="primary"
              size="medium"
              onClick={() => setConfigOpen(true)}
              sx={{
                position: 'absolute',
                bottom: 24,
                right: 24,
              }}
              aria-label="config"
            >
              <SettingsIcon />
            </Fab>
          )}
        </Box>

        {/* Config - fixed position, ไม่เลื่อนตาม content */}
        {configOpen && (
          <Box
            sx={{
              width: CONFIG_WIDTH,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Config Theme
              </Typography>
              <IconButton size="small" onClick={() => setConfigOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <PreviewThemeForm
                initialTheme={localTheme ?? theme}
                onChange={setLocalTheme}
                onSave={onSaveTheme ? handleSaveTheme : undefined}
                saving={saving}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
